// ============================================================================
// PMTiles共通ユーティリティ
// ----------------------------------------------------------------------------
// buildingTiles.ts / roadsParksTiles.ts で共通のタイル座標変換・PMTilesインス
// タンス管理をまとめる。自前ビルドのPMTilesはいずれもz12単一ズーム。
// ============================================================================

import { PMTiles } from "pmtiles";
import type { LngLat } from "./types";

export const TILE_ZOOM = 12;

const instances = new Map<string, PMTiles>();

/** URLごとにPMTilesインスタンスをキャッシュして使い回す。 */
export function getPMTiles(url: string): PMTiles {
  let p = instances.get(url);
  if (!p) {
    p = new PMTiles(url);
    instances.set(url, p);
  }
  return p;
}

export function lngToTileX(lng: number, z: number): number {
  return Math.floor(((lng + 180) / 360) * 2 ** z);
}
export function latToTileY(lat: number, z: number): number {
  const r = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z);
}
export function tileToLng(x: number, z: number): number {
  return (x / 2 ** z) * 360 - 180;
}
export function tileToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

/** タイルローカル座標(0..extent)を緯度経度に変換。 */
export function localToLngLat(
  x: number,
  y: number,
  tx: number,
  ty: number,
  z: number,
  extent: number
): LngLat {
  const west = tileToLng(tx, z);
  const east = tileToLng(tx + 1, z);
  const north = tileToLat(ty, z);
  const south = tileToLat(ty + 1, z);
  return [west + (x / extent) * (east - west), north + (y / extent) * (south - north)];
}

/** bbox [south, west, north, east] にかかるzoomタイルの(tx,ty)一覧。 */
export function tilesForBbox(
  south: number,
  west: number,
  north: number,
  east: number,
  zoom: number
): { tx: number; ty: number }[] {
  const x0 = lngToTileX(west, zoom);
  const x1 = lngToTileX(east, zoom);
  const y0 = latToTileY(north, zoom);
  const y1 = latToTileY(south, zoom);
  const out: { tx: number; ty: number }[] = [];
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) out.push({ tx, ty });
  }
  return out;
}

/**
 * タイル(tx,ty)の範囲と点ptとの最短距離（度、緯度経度混在の粗い近似で並べ替え用途
 * には十分）。点がタイル内ならちょうど0になる。
 *
 * タイルを「近い順」に並べる基準として、以前はタイル中心との距離を使っていたが、
 * z12タイルは一辺約7〜8kmもあるため、打ち上げ地点がタイルの端に近い場合、実際に
 * 隣接して民家が密集しているタイルの方が「中心距離」では遠い扱いになり、後回しに
 * された挙句デコード件数の上限で読まれずに終わることがあった(実例: 江戸川区花火
 * 大会で、隣接する高密度タイルが処理順5番目になり一度もデコードされなかった)。
 * タイル境界までの最短距離なら、打ち上げ地点に隣接するタイルは常にほぼ0になり、
 * 本当に離れたタイルとの区別がつく。
 */
export function tileBboxDistance(pt: LngLat, tx: number, ty: number, z: number): number {
  const west = tileToLng(tx, z);
  const east = tileToLng(tx + 1, z);
  const north = tileToLat(ty, z);
  const south = tileToLat(ty + 1, z);
  const dx = Math.max(west - pt[0], 0, pt[0] - east);
  const dy = Math.max(south - pt[1], 0, pt[1] - north);
  return Math.hypot(dx, dy);
}

/** リングの面積（絶対値。穴・複数パーツの中から最大の外周を選ぶ用）。 */
export function ringArea(ring: LngLat[]): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
}

export function pointInBbox(
  pt: LngLat,
  south: number,
  west: number,
  north: number,
  east: number
): boolean {
  return pt[0] >= west && pt[0] <= east && pt[1] >= south && pt[1] <= north;
}

export function ringIntersectsBbox(
  ring: LngLat[],
  south: number,
  west: number,
  north: number,
  east: number
): boolean {
  for (const pt of ring) {
    if (pointInBbox(pt, south, west, north, east)) return true;
  }
  return false;
}
